/* eslint-disable @typescript-eslint/ban-ts-comment */
import download from 'downloadjs';
import { polyfill } from 'es6-promise';

import 'whatwg-fetch';

polyfill();

const UNAUTHORIZED = 401;
const NO_CONTENT = 204;

type Config = {
  basePath: string;
  agent: (input: RequestInfo, options: RequestInit) => Promise<Response>;
  errorCatcher: (err: Error) => void;
};

type Options = {
  headers: Record<string, string>;
  method: RequestTypes;
  body?: string;
};

enum RequestTypes {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

let config: Config = {
  basePath: '/api',
  agent: window.fetch.bind(window),
  errorCatcher: (err: Error) => console.log('log error:', err),
};

const createEndpoint = (endpoint: string) => `${config.basePath}${endpoint}`;

const createOptions = (method: RequestTypes, body?: string): Options => {
  const defaultOptions = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method,
  };

  if (method === RequestTypes.GET) {
    return defaultOptions;
  }

  return {
    ...defaultOptions,
    body,
  };
};

const findFileName = (contentDispositionParts: string[]): string | undefined =>
  decodeFileNameStar(
    contentDispositionParts
      .find((n) => n.trim().startsWith('filename*='))
      ?.trim()
      .substring('filename*='.length),
  ) ||
  contentDispositionParts
    .find((n) => n.trim().startsWith('filename='))
    ?.trim()
    .substring('filename='.length)
    .replace(/^"(.*)"$/, '$1');

const decodeFileNameStar = (fileNameStar: string | undefined): string | undefined => {
  const parts = fileNameStar?.split("'");

  if (parts?.length !== 3 || parts[0].toUpperCase() !== 'UTF-8') {
    return undefined;
  }

  return decodeURIComponent(parts[2]);
};

const handleFileDownload = async (res: Response) => {
  const contentDisposition = res.headers.get('content-disposition');
  if (contentDisposition) {
    const filename = findFileName(contentDisposition.split(';')) || 'download';
    const blob = await res.blob();

    download(blob, filename);

    return res;
  }

  return {};
};

const handleResponse = async (res: Response): Promise<unknown> => {
  if (res.status === NO_CONTENT) return {};

  if (!res.ok) {
    try {
      return await res.json();
    } catch {
      const errorText = await res.text().catch(() => '');

      throw new Error(
        JSON.stringify({
          type: 'api',
          url: res.url,
          status: res.status,
          message: errorText || 'Ошибка парсинга респонса API',
          statusText: res.statusText,
        }),
      );
    }
  }

  if (res.headers.has('content-disposition')) {
    return handleFileDownload(res);
  }

  try {
    return await res.json();
  } catch {
    return {};
  }
};

export const configure = (cfg: Partial<Config>): Config => (config = { ...config, ...cfg });

export const request = async <T = unknown>(url: string, options: object): Promise<T> => {
  try {
    const response = await config.agent(createEndpoint(url), options);
    return (await handleResponse(response)) as T;
  } catch (err) {
    if (err instanceof Error) {
      try {
        const errorData = JSON.parse(err.message);
        if (errorData.status === UNAUTHORIZED) {
          window?.dispatchEvent(new Event('logout'));
        }
      } catch {}
    }
    throw err;
  }
};

export const createRequestByType =
  (type: RequestTypes) =>
  <T = unknown>(endpoint: string, body?: object | string): Promise<T> => {
    const options = createOptions(type, body ? JSON.stringify(body) : undefined);
    return request<T>(endpoint, options);
  };

export const get = createRequestByType(RequestTypes.GET);
export const post = createRequestByType(RequestTypes.POST);
export const put = createRequestByType(RequestTypes.PUT);
export const deleteMethod = createRequestByType(RequestTypes.DELETE);
